import React from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * OperationComparisonView — pinta un `OperationComparison` (§8.4) por BLOQUES. Puramente
 * presentacional: la comparación se muestra tal y como la manda Sun.
 *
 * Lo comparten los tres sitios que enseñan diferencias — la previsualización de publicación (C4,
 * N16), el impacto de una actualización (C6, N14) y la comparación de dos versiones (C10, N7) — igual
 * que `BomComparisonView` en #117. No está entre los catorce ficheros congelados de §8.7 porque la
 * spec no previó que los tres necesitaran el mismo trozo; se declara como pieza añadida.
 *
 * Ajuste del PO (2026-09-16): cada grupo de cambios es una `SmartMateriaUITable` compacta —densa, sin
 * buscador ni paginación y con todas las filas en una página—, exactamente como la comparación del
 * BOM. Los cambios se leen en columnas, no como una lista de frases concatenadas.
 *
 * ⚠️ El bloque `DOCUMENTS` viaja SIEMPRE vacío (`contrato-sun.md` §7): el `JobReference` de dominio
 * del editor no lleva documentos. Aquí no se promete nada sobre él — los bloques sin diferencias
 * sencillamente no se pintan.
 */
const BLOCK_TITLES = {
  GENERAL: 'General properties',
  PREPARATION: 'Preparation',
  TASKS: 'Tasks',
  FEATURES: 'Features',
  TOOLS: 'Tools',
  DOCUMENTS: 'Documents'
}

const isEmptyBlock = (block) => (
  (block?.added?.length || 0) === 0 &&
  (block?.removed?.length || 0) === 0 &&
  (block?.modified?.length || 0) === 0
)

const formatValue = (value) => (value === null || value === undefined || value === '' ? '—' : String(value))

/** Props comunes a los tres grupos: tabla compacta, de solo lectura y sin paginar. */
const compactTableProps = {
  dense: true,
  sticky: false,
  searchAndPaginationVisible: 'none',
  tablePageSize: Number.MAX_SAFE_INTEGER,
  primaryKeys: ['id'],
  sortOptions: { disableAutoSort: true }
}

const BlockTitle = ({ title, color, count }) => (
  <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
    <Typography variant='caption'>{title}</Typography>
    <Chip size='small' color={color} label={count} />
  </Stack>
)

const ItemsBlock = ({ testId, title, color, items, t_ }) => {
  if (!items || items.length === 0) return null
  // Se respeta el orden en que Sun los manda (la tabla tiene el orden automático desactivado); el id
  // es solo la clave de fila.
  const rows = items.map((item, index) => ({ ...item, id: item.key + '#' + index }))
  const columns = [
    { name: t_('Name'), field: 'label', readOnly: true, render: (row) => formatValue(row.label) },
    { name: t_('Value'), field: 'detail', readOnly: true, render: (row) => formatValue(row.detail) }
  ]
  return (
    <Box data-testid={testId}>
      <BlockTitle title={title} color={color} count={items.length} />
      <SmartMateriaUITable {...compactTableProps} columns={columns} dataFetch={() => Promise.resolve(rows)} />
    </Box>
  )
}

const ModifiedBlock = ({ items, t_ }) => {
  if (!items || items.length === 0) return null
  // Una fila por CAMPO cambiado de cada elemento: es lo que se compara, y así el antes y el después
  // quedan uno al lado del otro en vez de dentro de una frase.
  const rows = items.flatMap((item, index) => (item.changedFields || []).map((field) => ({
    id: item.key + '#' + index + '#' + field,
    label: item.label,
    field,
    before: item.before?.detail ?? item.before?.label,
    after: item.after?.detail ?? item.after?.label
  })))
  const columns = [
    { name: t_('Name'), field: 'label', readOnly: true, render: (row) => formatValue(row.label) },
    { name: t_('Field'), field: 'field', readOnly: true },
    { name: t_('Before'), field: 'before', readOnly: true, render: (row) => formatValue(row.before) },
    { name: t_('After'), field: 'after', readOnly: true, render: (row) => formatValue(row.after) }
  ]
  return (
    <Box data-testid='operation-comparison-modified'>
      <BlockTitle title={t_('Modified')} color='warning' count={items.length} />
      <SmartMateriaUITable {...compactTableProps} columns={columns} dataFetch={() => Promise.resolve(rows)} />
    </Box>
  )
}

const OperationComparisonView = ({ comparison }) => {
  const { t_ } = useSmartTranslation()
  if (!comparison) return null

  const blocks = (comparison.blocks || []).filter((block) => !isEmptyBlock(block))

  return (
    <Stack spacing={2} data-testid='operation-comparison'>
      <Typography variant='subtitle2' data-testid='operation-comparison-versions'>
        {t_('Version')} {comparison.fromVersionNumber} → {t_('Version')} {comparison.toVersionNumber}
      </Typography>
      {blocks.length === 0
        ? <Typography variant='body2'>{t_('No differences between the compared versions')}</Typography>
        : blocks.map((block) => (
          <Stack key={block.block} spacing={1} data-testid={'operation-comparison-block-' + block.block}>
            <Typography variant='subtitle2'>{t_(BLOCK_TITLES[block.block] || block.block)}</Typography>
            <ItemsBlock testId='operation-comparison-added' title={t_('Added')} color='success' items={block.added} t_={t_} />
            <ItemsBlock testId='operation-comparison-removed' title={t_('Removed')} color='error' items={block.removed} t_={t_} />
            <ModifiedBlock items={block.modified} t_={t_} />
          </Stack>
        ))}
    </Stack>
  )
}

export default OperationComparisonView
